import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { useAthlete } from '@/context/AthleteContext';
import { Target, Video, ClipboardList, Calendar, ArrowRight, Ticket, CreditCard, CheckCircle, AlertCircle, TrendingUp, Award, Star, UserPlus, X, RefreshCw, Edit2, Save, ChevronDown, ChevronRight, Users, Package, DollarSign, Clock, AlertTriangle, Mail, Building2, Shield, ExternalLink, Layers, User, Sparkles, ArrowUpCircle, Check } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ParentDashboard = () => {
  const { user, getAuthHeaders } = useAuth();
  const { theme } = useTheme();
  const { 
    athletes, 
    athlete, 
    athletePass, 
    athletePasses,
    selectedAthleteIndex, 
    selectAthlete, 
    refreshAthletes,
    hasMultipleAthletes 
  } = useAthlete();
  const navigate = useNavigate();
  
  // Context selector state (per athlete): 'all' | 'team' | 'private'
  const [selectedContext, setSelectedContext] = useState({});
  
  // Per-athlete data
  const [athleteTeams, setAthleteTeams] = useState({}); // {athleteId: teamsData}
  const [sessions, setSessions] = useState([]);
  const [trainingPlan, setTrainingPlan] = useState(null);
  const [techDev, setTechDev] = useState(null);
  
  // UI state
  const [loading, setLoading] = useState(true);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [showInviteCoach, setShowInviteCoach] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteProcessing, setInviteProcessing] = useState(false);
  const [editingAthlete, setEditingAthlete] = useState(false);
  const [athleteForm, setAthleteForm] = useState({});
  const [showAthleteSelector, setShowAthleteSelector] = useState(false);
  
  // Coach change request state
  const [changeRequest, setChangeRequest] = useState(null);
  const [showConfirmMigration, setShowConfirmMigration] = useState(false);
  const [migrationProcessing, setMigrationProcessing] = useState(false);
  
  // Expiring plans state
  const [expiringPlans, setExpiringPlans] = useState([]);
  const [showExpiringPlanModal, setShowExpiringPlanModal] = useState(false);
  const [dismissedExpiringPlans, setDismissedExpiringPlans] = useState(() => {
    // Load dismissed plans from localStorage
    const stored = localStorage.getItem('dismissedExpiringPlans');
    return stored ? JSON.parse(stored) : [];
  });

  // Get contexts available for current athlete
  const getAthleteContexts = (ath) => {
    if (!ath) return [];
    const contexts = [];
    
    // Check if has private coach
    if (ath.coach_id) {
      contexts.push({
        type: 'private',
        label: 'Private Coach',
        icon: 'user',
        description: ath.coach_name || 'Personal Training'
      });
    }
    
    // Check if has club teams
    const teams = athleteTeams[ath.id] || [];
    teams.forEach(team => {
      contexts.push({
        type: 'team',
        teamId: team.team_id,
        label: team.team_name,
        icon: 'shield',
        description: team.club_name,
        category: team.category
      });
    });
    
    return contexts;
  };
  
  const currentAthleteContexts = athlete ? getAthleteContexts(athlete) : [];
  const currentContext = selectedContext[athlete?.id] || (currentAthleteContexts[0]?.type === 'team' ? `team:${currentAthleteContexts[0]?.teamId}` : 'private');
  
  // Helper to determine if showing team context
  const isTeamContext = currentContext?.startsWith('team:');
  const currentTeamId = isTeamContext ? currentContext.split(':')[1] : null;
  const currentTeam = currentTeamId ? athleteTeams[athlete?.id]?.find(t => t.team_id === currentTeamId) : null;

  // Check for expiring plans
  const checkExpiringPlans = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/parent/expiring-plans`, { headers: getAuthHeaders() });
      if (res.data.has_expiring_plans) {
        // Filter out dismissed plans
        const activePlans = res.data.expiring_plans.filter(
          plan => !dismissedExpiringPlans.includes(`${plan.athlete_id}_${plan.plan_end_date}`)
        );
        setExpiringPlans(activePlans);
        // Show modal only if there are critical or warning plans
        if (activePlans.some(p => p.urgency === 'critical' || p.urgency === 'warning')) {
          setShowExpiringPlanModal(true);
        }
      }
    } catch (error) {
      console.error('Failed to check expiring plans:', error);
    }
  }, [getAuthHeaders, dismissedExpiringPlans]);

  const dismissExpiringPlan = (plan) => {
    const key = `${plan.athlete_id}_${plan.plan_end_date}`;
    const newDismissed = [...dismissedExpiringPlans, key];
    setDismissedExpiringPlans(newDismissed);
    localStorage.setItem('dismissedExpiringPlans', JSON.stringify(newDismissed));
    
    setExpiringPlans(prev => prev.filter(p => 
      `${p.athlete_id}_${p.plan_end_date}` !== key
    ));
    
    // Close modal if no more plans
    if (expiringPlans.length <= 1) {
      setShowExpiringPlanModal(false);
    }
  };

  const fetchData = async () => {
    if (!athlete) {
      setLoading(false);
      return;
    }
    
    try {
      // Fetch athlete-specific data
      await fetchAthleteData(athlete.id);
      // Fetch change request status for this athlete
      await fetchChangeRequestStatus(athlete.id);
      // Initialize form
      setAthleteForm(athlete);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
    setLoading(false);
  };

  const fetchChangeRequestStatus = async (athleteId) => {
    try {
      const changeRes = await axios.get(`${API}/parent/change-request-status?athlete_id=${athleteId}`, { headers: getAuthHeaders() });
      if (changeRes.data.has_request) {
        setChangeRequest(changeRes.data.request);
      } else {
        setChangeRequest(null);
      }
    } catch (e) {
      setChangeRequest(null);
    }
  };

  const fetchAthleteData = async (athleteId) => {
    try {
      const [sessionsRes, planRes, teamsRes, techDevRes] = await Promise.all([
        axios.get(`${API}/athletes/${athleteId}/sessions`, { headers: getAuthHeaders() }).catch(() => ({ data: [] })),
        axios.get(`${API}/athletes/${athleteId}/training-plan`, { headers: getAuthHeaders() }).catch(() => ({ data: null })),
        axios.get(`${API}/parent/athlete/${athleteId}/teams`, { headers: getAuthHeaders() }).catch(() => ({ data: [] })),
        axios.get(`${API}/athletes/${athleteId}/technical-development`, { headers: getAuthHeaders() }).catch(() => ({ data: null }))
      ]);
      setSessions(sessionsRes.data);
      setTrainingPlan(planRes.data);
      setAthleteTeams(prev => ({ ...prev, [athleteId]: teamsRes.data }));
      setTechDev(techDevRes.data);
    } catch (error) {
      console.error('Failed to fetch athlete data:', error);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Check for expiring plans on load
  useEffect(() => {
    checkExpiringPlans();
  }, [checkExpiringPlans]);

  // When selected athlete changes, fetch their data
  useEffect(() => {
    if (athlete) {
      setAthleteForm(athlete);
      fetchAthleteData(athlete.id);
      fetchChangeRequestStatus(athlete.id);
      setEditingAthlete(false);
    }
  }, [athlete]);

  const handleSelectAthlete = (index) => {
    selectAthlete(index);
    setShowAthleteSelector(false);
    setEditingAthlete(false);
    setChangeRequest(null);
  };

  // Payment confirmation modal state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [checkoutUrl, setCheckoutUrl] = useState(null);

  const handlePayAthletePass = async (passType = 'standard') => {
    if (!athlete) return;
    setPaymentProcessing(true);
    try {
      // Create Stripe Checkout session for the selected athlete
      const response = await axios.post(`${API}/parent/checkout`, {
        origin_url: window.location.origin,
        pass_type: passType,
        athlete_id: athlete.id
      }, { headers: getAuthHeaders() });
      
      if (response.data.checkout_url) {
        setCheckoutUrl(response.data.checkout_url);
        setShowPaymentModal(true);
        setShowUpgradeModal(false);
        setPaymentProcessing(false);
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setPaymentProcessing(false);
    }
  };

  const proceedToStripe = () => {
    if (checkoutUrl) {
      window.location.href = checkoutUrl;
    }
  };

  const handleInviteCoach = async (e) => {
    e.preventDefault();
    if (!inviteEmail || !athlete) return;
    setInviteProcessing(true);
    try {
      const res = await axios.post(`${API}/parent/invite-coach`, { 
        coach_email: inviteEmail,
        athlete_id: athlete.id 
      }, { headers: getAuthHeaders() });
      toast.success(res.data.message);
      setInviteEmail('');
      setShowInviteCoach(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to invite coach');
    }
    setInviteProcessing(false);
  };

  const handleConfirmMigration = async (confirm) => {
    if (!changeRequest) return;
    setMigrationProcessing(true);
    try {
      const res = await axios.post(
        `${API}/parent/confirm-coach-change/${changeRequest.id}?confirm=${confirm}`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success(res.data.message);
      setShowConfirmMigration(false);
      setChangeRequest(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao processar');
    }
    setMigrationProcessing(false);
  };

  const handleCancelChangeRequest = async () => {
    if (!athlete) return;
    try {
      await axios.post(`${API}/parent/cancel-change-request?athlete_id=${athlete.id}`, {}, { headers: getAuthHeaders() });
      toast.success('Request cancelled');
      setChangeRequest(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel');
    }
  };

  const handleSaveAthlete = async () => {
    if (!athlete) return;
    try {
      await axios.put(`${API}/athletes/${athlete.id}`, athleteForm, { headers: getAuthHeaders() });
      toast.success('Athlete profile updated');
      setEditingAthlete(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (athletes.length === 0) {
    return (
      <div className="text-center py-12">
        <Users className="mx-auto h-16 w-16 text-muted-foreground mb-4" />
        <h2 className="font-heading text-2xl uppercase mb-2">No Athletes Found</h2>
        <p className="text-muted-foreground">You don't have any athletes registered yet.</p>
      </div>
    );
  }

  // Club athletes are NOT independent (they belong to a club structure)
  // Independent = has no coach_id AND is not a club athlete
  const isIndependent = athlete && !athlete.coach_id && !athlete.is_club_athlete;
  const hasActivePass = athletePass?.status === 'active';
  const needsPayment = athletePass?.status === 'pending' || athletePass?.status === 'expired' || !athletePass;
  
  // Determine if pass is Plus (either by pass_type or independent athlete)
  const isPassPlus = athletePass?.pass_type === 'plus' || athletePass?.plus_status === 'active';
  const passPrice = (isIndependent || isPassPlus) ? '$14.99' : '$9.99';
  const passLabel = (isIndependent || isPassPlus) ? 'Athlete Pass Plus' : 'Athlete Pass';

  const getPassStatusInfo = () => {
    if (!athletePass || athletePass.status === 'none') return { status: 'none', color: 'bg-muted', text: 'No Pass' };
    switch (athletePass.status) {
      case 'active':
        // Add "Plus" indicator if the pass is Plus type
        const plusIndicator = isPassPlus ? ' Plus' : '';
        return { status: 'active', color: 'bg-green-500', text: `Active${plusIndicator}` };
      case 'pending':
        return { status: 'pending', color: 'bg-yellow-500', text: 'Payment Required' };
      case 'expired':
        return { status: 'expired', color: 'bg-red-500', text: 'Expired' };
      default:
        return { status: 'none', color: 'bg-muted', text: 'No Pass' };
    }
  };

  const passInfo = getPassStatusInfo();

  // Training Plan Info Helper
  const getPlanInfo = () => {
    if (!athlete) return null;
    
    const planTypes = {
      'monthly': 'Monthly',
      'quarterly': 'Quarterly',
      'package': 'Session Package',
      'weekly': 'Weekly'
    };
    
    const planName = planTypes[athlete.plan_type] || athlete.plan_type || 'Not defined';
    const planValue = athlete.plan_value || 0;
    const planStartDate = athlete.plan_start_date ? new Date(athlete.plan_start_date) : null;
    const planEndDate = athlete.plan_end_date ? new Date(athlete.plan_end_date) : null;
    const paymentStatus = athlete.payment_status || 'pending';
    const sessionsRemaining = athlete.sessions_remaining || 0;
    const sessionsTotal = athlete.sessions_total || 0;
    
    // Calculate if plan is valid
    const now = new Date();
    let validityStatus = 'unknown';
    let daysRemaining = null;
    
    if (planEndDate) {
      if (planEndDate > now) {
        validityStatus = 'active';
        daysRemaining = Math.ceil((planEndDate - now) / (1000 * 60 * 60 * 24));
      } else {
        validityStatus = 'expired';
      }
    }
    
    return {
      planName,
      planValue,
      planStartDate,
      planEndDate,
      paymentStatus,
      sessionsRemaining,
      sessionsTotal,
      validityStatus,
      daysRemaining
    };
  };
  
  const planInfo = getPlanInfo();

  const quickLinks = [
    { path: '/dashboard/technical', icon: Target, label: 'Technical Development', desc: 'View skill progression' },
    { path: '/dashboard/training-plan', icon: ClipboardList, label: 'Training Plan', desc: 'Current training focus' },
    { path: '/dashboard/videos', icon: Video, label: 'Video Analysis', desc: 'Game footage and feedback' },
    { path: '/dashboard/sessions', icon: Calendar, label: 'Sessions', desc: 'Session history' },
  ];

  // Progress metrics
  const completedSessions = sessions.filter(s => s.status === 'completed').length;
  const scheduledSessions = sessions.filter(s => s.status === 'scheduled').length;
  const totalSessions = sessions.length;

  const getMilestones = () => {
    return [
      { id: 1, name: 'First Session', target: 1, icon: '🎯', achieved: completedSessions >= 1 },
      { id: 2, name: '5 Sessions', target: 5, icon: '⭐', achieved: completedSessions >= 5 },
      { id: 3, name: '10 Sessions', target: 10, icon: '🏆', achieved: completedSessions >= 10 },
      { id: 4, name: '25 Sessions', target: 25, icon: '🥇', achieved: completedSessions >= 25 },
    ];
  };
  
  const milestones = getMilestones();
  const nextMilestone = milestones.find(m => !m.achieved) || milestones[milestones.length - 1];
  const achievedMilestones = milestones.filter(m => m.achieved).length;

  return (
    <div className="space-y-4 md:space-y-8" data-testid="parent-dashboard">
      {/* Expiring Plan Modal */}
      {showExpiringPlanModal && expiringPlans.length > 0 && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="expiring-plan-modal">
          <div className="bg-card border border-amber-500/50 rounded-sm max-w-lg w-full p-6 relative max-h-[90vh] overflow-y-auto">
            <button 
              onClick={() => setShowExpiringPlanModal(false)}
              className="absolute top-4 right-4 text-muted-foreground hover:text-foreground"
              data-testid="close-expiring-modal-btn"
            >
              <X size={20} />
            </button>
            
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="text-amber-400" size={32} />
              </div>
              <h2 className="font-heading text-xl uppercase mb-2">Plan(s) Expiring</h2>
              <p className="text-muted-foreground text-sm">
                One or more training plans are approaching expiration.
              </p>
            </div>

            <div className="space-y-4">
              {expiringPlans.map((plan, index) => (
                <div 
                  key={`${plan.athlete_id}-${index}`}
                  className={`p-4 rounded-sm border ${
                    plan.urgency === 'critical' 
                      ? 'bg-red-500/10 border-red-500/50' 
                      : plan.urgency === 'warning'
                      ? 'bg-amber-500/10 border-amber-500/50'
                      : 'bg-muted/30 border-border'
                  }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-lg">{plan.athlete_name}</h3>
                      <p className="text-sm text-muted-foreground">Plan: {plan.plan_type}</p>
                    </div>
                    <div className={`text-right ${
                      plan.is_expired ? 'text-red-400' : 
                      plan.urgency === 'critical' ? 'text-red-400' : 'text-amber-400'
                    }`}>
                      {plan.is_expired ? (
                        <>
                          <p className="text-2xl font-bold">Expired</p>
                          <p className="text-xs">{Math.abs(plan.days_remaining)} days ago</p>
                        </>
                      ) : (
                        <>
                          <p className="text-2xl font-bold">{plan.days_remaining}</p>
                          <p className="text-xs">days remaining</p>
                        </>
                      )}
                    </div>
                  </div>
                  
                  <div className="text-sm text-muted-foreground mb-3">
                    <p>Expires on: <span className="text-foreground">{new Date(plan.plan_end_date).toLocaleDateString('en-US')}</span></p>
                    {plan.plan_value > 0 && (
                      <p>Value: <span className="text-foreground">${plan.plan_value.toFixed(2)}</span></p>
                    )}
                  </div>

                  <div className="bg-muted/50 rounded-sm p-3 mb-3">
                    <p className="text-xs text-muted-foreground mb-1">To renew, contact:</p>
                    <p className="font-medium text-sm">{plan.coach_name}</p>
                    {plan.coach_email && (
                      <a 
                        href={`mailto:${plan.coach_email}?subject=Plan Renewal - ${plan.athlete_name}`}
                        className="text-xs text-primary hover:underline flex items-center gap-1 mt-1"
                      >
                        <Mail size={12} />
                        {plan.coach_email}
                      </a>
                    )}
                  </div>

                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full text-xs text-muted-foreground hover:text-foreground"
                    onClick={() => dismissExpiringPlan(plan)}
                  >
                    Don't show again for this plan
                  </Button>
                </div>
              ))}
            </div>

            <div className="mt-6 pt-4 border-t border-border">
              <Button 
                onClick={() => setShowExpiringPlanModal(false)}
                className="w-full bg-primary text-black hover:bg-primary/90"
              >
                Got it
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Test Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-primary/30 rounded-sm max-w-md w-full p-6 relative">
            <button 
              onClick={() => setShowPaymentModal(false)}
              className="absolute top-4 right-4 text-muted-foreground hover:text-foreground"
            >
              <X size={20} />
            </button>
            
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CreditCard className="text-amber-400" size={32} />
              </div>
              <h2 className="font-heading text-xl uppercase mb-2">Test Payment Mode</h2>
              <p className="text-muted-foreground text-sm">
                This is a <span className="text-amber-400 font-medium">simulated payment</span>. 
                No real charges will be made.
              </p>
            </div>

            <div className="bg-muted/30 border border-border rounded-sm p-4 mb-6">
              <p className="text-sm font-medium mb-3 text-center">Use these test card details:</p>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Card Number:</span>
                  <code className="bg-primary/10 text-primary px-2 py-1 rounded font-mono">4242 4242 4242 4242</code>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Expiry Date:</span>
                  <code className="bg-primary/10 text-primary px-2 py-1 rounded font-mono">12/30</code>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">CVC:</span>
                  <code className="bg-primary/10 text-primary px-2 py-1 rounded font-mono">123</code>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Name:</span>
                  <code className="bg-primary/10 text-primary px-2 py-1 rounded font-mono">Any Name</code>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <Button 
                onClick={proceedToStripe}
                className="w-full bg-primary text-black hover:bg-primary/90 h-12"
              >
                Continue to Payment
              </Button>
              <Button 
                variant="ghost" 
                onClick={() => setShowPaymentModal(false)}
                className="w-full"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Upgrade to Plus Modal */}
      {showUpgradeModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="upgrade-modal">
          <div className="bg-card border border-purple-500/30 rounded-sm max-w-lg w-full p-6 relative">
            <button 
              onClick={() => setShowUpgradeModal(false)}
              className="absolute top-4 right-4 text-muted-foreground hover:text-foreground"
            >
              <X size={20} />
            </button>
            
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-purple-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Sparkles className="text-purple-400" size={32} />
              </div>
              <h2 className="font-heading text-xl uppercase mb-2">
                {passInfo.status === 'active' ? 'Upgrade to Plus' : 'Get Athlete Pass Plus'}
              </h2>
              <p className="text-muted-foreground text-sm">
                Unlock advanced features for <span className="text-primary font-medium">{athlete?.name}</span>
              </p>
            </div>

            {/* Current Plan - Only show if pass is active */}
            {passInfo.status === 'active' && (
              <div className="bg-muted/30 border border-border rounded-sm p-4 mb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Current Plan</p>
                    <p className="font-medium">Athlete Pass</p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-heading text-primary">$9.99</p>
                    <p className="text-xs text-muted-foreground">/month</p>
                  </div>
                </div>
              </div>
            )}

            {/* Plus Plan */}
            <div className="bg-purple-500/10 border border-purple-500/30 rounded-sm p-4 mb-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Star className="text-purple-400" size={18} />
                  <div>
                    <p className="font-medium">Athlete Pass Plus</p>
                    <p className="text-xs text-muted-foreground">Premium features</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-lg font-heading text-purple-400">$14.99</p>
                  <p className="text-xs text-muted-foreground">/month</p>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <Check className="text-purple-400 mt-0.5 flex-shrink-0" size={16} />
                  <div>
                    <p className="text-sm font-medium">Access to Multiple Coaches</p>
                    <p className="text-xs text-muted-foreground">
                      Unified profile combining Private Soccer Coach, Performance Coach and (when enrolled) Club Coach evaluations
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="text-purple-400 mt-0.5 flex-shrink-0" size={16} />
                  <div>
                    <p className="text-sm font-medium">Free Private Coach Invitations</p>
                    <p className="text-xs text-muted-foreground">
                      Invite Private Soccer Coaches and Performance Coaches to evaluate your athlete at no additional cost
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="text-purple-400 mt-0.5 flex-shrink-0" size={16} />
                  <div>
                    <p className="text-sm font-medium">Full Development History Ownership</p>
                    <p className="text-xs text-muted-foreground">
                      Your athlete's progress stays with you when transitioning between coaches
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Pricing explanation for upgrades */}
            {passInfo.status === 'active' && (
              <div className="bg-green-500/10 border border-green-500/30 rounded-sm p-3 mb-4">
                <p className="text-sm text-green-400 text-center">
                  <strong>Upgrade Price: $4.99</strong> — Pay only the difference for this billing cycle
                </p>
              </div>
            )}

            <div className="space-y-3">
              <Button 
                onClick={() => handlePayAthletePass(passInfo.status === 'active' ? 'upgrade_to_plus' : 'plus')}
                disabled={paymentProcessing}
                className="w-full bg-purple-500 text-white hover:bg-purple-600 h-12"
              >
                <ArrowUpCircle size={16} className="mr-2" />
                {paymentProcessing ? 'Processing...' : (
                  passInfo.status === 'active' 
                    ? 'Upgrade to Plus - $4.99' 
                    : 'Get Plus - $14.99/month'
                )}
              </Button>
              <Button 
                variant="ghost" 
                onClick={() => setShowUpgradeModal(false)}
                className="w-full"
              >
                Maybe Later
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Header with Athlete Selector */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl uppercase tracking-tight">Welcome, {user?.full_name}</h1>
          <p className="text-muted-foreground text-sm md:text-base">
            {isIndependent ? 'Manage your athlete development' : 'Track your athlete\'s development'}
          </p>
        </div>

        {/* Athlete Selector - Only show if multiple athletes */}
        {athletes.length > 1 && (
          <div className="relative">
            <button
              onClick={() => setShowAthleteSelector(!showAthleteSelector)}
              className="flex items-center gap-3 px-4 py-3 bg-card border border-border rounded-sm hover:border-primary/50 transition-colors min-w-[200px]"
              data-testid="athlete-selector-btn"
            >
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                <span className="text-primary font-bold text-sm">{athlete?.name?.[0]}</span>
              </div>
              <div className="flex-1 text-left">
                <p className="font-medium text-sm truncate">{athlete?.name}</p>
                <p className="text-xs text-muted-foreground">
                  {athletePasses[athlete?.id]?.status === 'active' ? (
                    <span className="text-green-400">Pass Active</span>
                  ) : (
                    <span className="text-yellow-400">Pass Required</span>
                  )}
                </p>
              </div>
              <ChevronDown size={16} className={`text-muted-foreground transition-transform ${showAthleteSelector ? 'rotate-180' : ''}`} />
            </button>

            {/* Dropdown */}
            {showAthleteSelector && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-card border border-border rounded-sm shadow-lg z-50 overflow-hidden">
                <div className="p-2 border-b border-border bg-muted/30">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Select Athlete</p>
                </div>
                {athletes.map((ath, index) => {
                  const pass = athletePasses[ath.id];
                  const isActive = pass?.status === 'active';
                  return (
                    <button
                      key={ath.id}
                      onClick={() => handleSelectAthlete(index)}
                      className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-muted/50 transition-colors ${
                        index === selectedAthleteIndex ? 'bg-primary/10' : ''
                      }`}
                      data-testid={`athlete-option-${index}`}
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        isActive ? 'bg-green-500/20' : 'bg-yellow-500/20'
                      }`}>
                        <span className={`font-bold text-sm ${isActive ? 'text-green-400' : 'text-yellow-400'}`}>
                          {ath.name?.[0]}
                        </span>
                      </div>
                      <div className="flex-1 text-left">
                        <p className="font-medium text-sm">{ath.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {ath.age} years • {ath.sport || 'Soccer'}
                        </p>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        isActive ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'
                      }`}>
                        {isActive ? 'Active' : 'Pending'}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Inline Expiring Plan Alert */}
      {expiringPlans.length > 0 && (
        <div 
          className="bg-amber-500/10 border border-amber-500/50 rounded-sm p-4 cursor-pointer hover:bg-amber-500/15 transition-colors"
          onClick={() => setShowExpiringPlanModal(true)}
          data-testid="expiring-plan-alert"
        >
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0">
              <AlertTriangle className="text-amber-400" size={24} />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-amber-400">
                {expiringPlans.length === 1 
                  ? 'Training Plan Expiring'
                  : `${expiringPlans.length} Training Plans Expiring`
                }
              </h3>
              <p className="text-sm text-muted-foreground">
                {expiringPlans.length === 1 
                  ? `${expiringPlans[0].athlete_name}'s plan ${expiringPlans[0].is_expired ? 'has expired' : `expires in ${expiringPlans[0].days_remaining} days`}. Contact your coach to renew.`
                  : 'Click to view details and contact your coach.'
                }
              </p>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
              onClick={(e) => {
                e.stopPropagation();
                setShowExpiringPlanModal(true);
              }}
            >
              View Details
            </Button>
          </div>
        </div>
      )}

      {/* Multi-Athlete Summary Cards (when multiple athletes) */}
      {athletes.length > 1 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {athletes.map((ath, index) => {
            const pass = athletePasses[ath.id];
            const isActive = pass?.status === 'active';
            const isSelected = index === selectedAthleteIndex;
            return (
              <button
                key={ath.id}
                onClick={() => handleSelectAthlete(index)}
                className={`p-3 rounded-sm border transition-all ${
                  isSelected 
                    ? 'border-primary bg-primary/5' 
                    : 'border-border/40 bg-card hover:border-primary/30'
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                    isActive ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'
                  }`}>
                    {ath.name?.[0]}
                  </div>
                  <span className="font-medium text-sm truncate">{ath.name}</span>
                </div>
                <div className={`text-xs px-2 py-0.5 rounded text-center ${
                  isActive ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'
                }`}>
                  {isActive ? '✓ Pass Active' : '⏳ Needs Pass'}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Selected Athlete Profile Card */}
      {athlete && (
        <div className="bg-card border border-border/40 p-4 md:p-6 rounded-sm">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-primary/10 flex items-center justify-center">
                <span className="text-primary font-bold text-xl md:text-2xl">{athlete.name?.[0]}</span>
              </div>
              <div>
                {editingAthlete ? (
                  <Input
                    value={athleteForm.name || ''}
                    onChange={(e) => setAthleteForm({...athleteForm, name: e.target.value})}
                    className="font-heading text-xl mb-1"
                    placeholder="Athlete name"
                  />
                ) : (
                  <h2 className="font-heading text-xl md:text-2xl">{athlete.name}</h2>
                )}
                {editingAthlete ? (
                  <div className="flex gap-2 mt-1">
                    <Input
                      value={athleteForm.position || ''}
                      onChange={(e) => setAthleteForm({...athleteForm, position: e.target.value})}
                      className="w-24 text-sm"
                      placeholder="Position"
                    />
                    <Input
                      type="number"
                      value={athleteForm.age || ''}
                      onChange={(e) => setAthleteForm({...athleteForm, age: parseInt(e.target.value)})}
                      className="w-16 text-sm"
                      placeholder="Age"
                    />
                  </div>
                ) : (
                  <p className="text-muted-foreground text-sm">
                    {athlete.position && `${athlete.position} • `}{athlete.age} years • {athlete.sport || 'Soccer'}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {editingAthlete ? (
                <>
                  <Button size="sm" variant="ghost" onClick={() => setEditingAthlete(false)}>
                    <X size={16} />
                  </Button>
                  <Button size="sm" onClick={handleSaveAthlete}>
                    <Save size={16} className="mr-1" /> Save
                  </Button>
                </>
              ) : (
                <Button size="sm" variant="ghost" onClick={() => setEditingAthlete(true)}>
                  <Edit2 size={16} className="mr-1" /> Edit
                </Button>
              )}
            </div>
          </div>

          {/* Coach Status */}
          <div className={`p-3 rounded-sm ${isIndependent ? 'bg-blue-500/10 border border-blue-500/30' : 'bg-green-500/10 border border-green-500/30'}`}>
            {isIndependent ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <UserPlus className="text-blue-400" size={18} />
                    <div>
                      <p className="text-sm font-medium text-blue-400">Coach Not on Meybra Yet</p>
                      <p className="text-xs text-muted-foreground">Invite your coach to unlock structured training plans</p>
                    </div>
                  </div>
                  <Button 
                    size="sm" 
                    onClick={() => setShowInviteCoach(true)}
                    className="bg-blue-500 text-white hover:bg-blue-400"
                    data-testid="invite-coach-btn"
                  >
                    <UserPlus size={14} className="mr-1" /> Invite Coach
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {/* Coach Photo */}
                  {theme.coachPhotoUrl ? (
                    <img 
                      src={theme.coachPhotoUrl.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${theme.coachPhotoUrl}` : theme.coachPhotoUrl}
                      alt={theme.coachName || 'Coach'}
                      className="w-12 h-12 rounded-full object-cover border-2 border-green-500/30"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center border-2 border-green-500/30">
                      <User className="text-green-400" size={24} />
                    </div>
                  )}
                  <div>
                    <p className="text-sm font-medium text-green-400">Coach Assigned</p>
                    <p className="text-xs text-foreground">{theme.coachName || theme.businessName}</p>
                    <p className="text-xs text-muted-foreground">Training is managed by your coach</p>
                  </div>
                </div>
                {/* Show Change Coach button only for Pass Plus users */}
                {isPassPlus && !changeRequest && (
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => setShowInviteCoach(true)}
                    className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
                    data-testid="change-coach-btn"
                  >
                    <RefreshCw size={14} className="mr-1" /> Change Coach
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Coach Change Request Status */}
      {changeRequest && (
        <div className={`border rounded-sm p-4 ${
          changeRequest.status === 'accepted_by_coach' 
            ? 'bg-gradient-to-r from-green-500/10 to-emerald-500/10 border-green-500/30'
            : 'bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border-blue-500/30'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                changeRequest.status === 'accepted_by_coach' 
                  ? 'bg-green-500/20' 
                  : 'bg-blue-500/20'
              }`}>
                {changeRequest.status === 'accepted_by_coach' ? (
                  <Check className="text-green-400" size={20} />
                ) : (
                  <Clock className="text-blue-400" size={20} />
                )}
              </div>
              <div>
                {changeRequest.status === 'pending_coach_acceptance' ? (
                  <>
                    <p className="font-medium text-blue-400">Waiting for Acceptance</p>
                    <p className="text-sm text-muted-foreground">
                      Invite sent to <strong>{changeRequest.new_coach_name}</strong>
                    </p>
                  </>
                ) : (
                  <>
                    <p className="font-medium text-green-400">Coach Accepted!</p>
                    <p className="text-sm text-muted-foreground">
                      <strong>{changeRequest.new_coach_name}</strong> wants to be your new coach
                    </p>
                  </>
                )}
              </div>
            </div>
            
            <div className="flex gap-2">
              {changeRequest.status === 'accepted_by_coach' ? (
                <Button
                  size="sm"
                  className="bg-green-600 hover:bg-green-700 text-white"
                  onClick={() => setShowConfirmMigration(true)}
                >
                  <ArrowRight size={14} className="mr-1" /> Confirm Migration
                </Button>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  className="text-red-400 border-red-400/50 hover:bg-red-500/10"
                  onClick={handleCancelChangeRequest}
                >
                  <X size={14} className="mr-1" /> Cancelar
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Context Switcher - Prominent selector for Team vs Private Coach */}
      {athlete && currentAthleteContexts.length > 1 && (
        <div className="bg-gradient-to-r from-primary/5 via-card to-blue-500/5 border-2 border-primary/20 p-4 md:p-5 rounded-sm" data-testid="context-selector">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center">
                <Layers className="text-primary" size={18} />
              </div>
              <div>
                <h3 className="font-heading text-sm uppercase tracking-wider">Select Training Context</h3>
                <p className="text-xs text-muted-foreground">{athlete.name} has {currentAthleteContexts.length} training environments</p>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {currentAthleteContexts.map((ctx, idx) => {
              const contextKey = ctx.type === 'team' ? `team:${ctx.teamId}` : 'private';
              const isActive = currentContext === contextKey;
              const isTeam = ctx.type === 'team';
              
              return (
                <button
                  key={idx}
                  onClick={() => {
                    setSelectedContext({...selectedContext, [athlete.id]: contextKey});
                    // Navigate to team view if selecting team context
                    if (isTeam && ctx.teamId) {
                      navigate(`/dashboard/team/${ctx.teamId}`);
                    }
                  }}
                  className={`relative flex items-center gap-4 p-4 rounded-sm border-2 transition-all ${
                    isActive 
                      ? isTeam 
                        ? 'border-blue-500 bg-blue-500/10 shadow-lg shadow-blue-500/10' 
                        : 'border-emerald-500 bg-emerald-500/10 shadow-lg shadow-emerald-500/10'
                      : 'border-border/40 bg-card hover:border-primary/50 hover:bg-muted/30'
                  }`}
                  data-testid={`context-btn-${ctx.type}-${idx}`}
                >
                  {/* Active Indicator */}
                  {isActive && (
                    <div className={`absolute top-2 right-2 w-2 h-2 rounded-full animate-pulse ${
                      isTeam ? 'bg-blue-500' : 'bg-emerald-500'
                    }`} />
                  )}
                  
                  {/* Icon */}
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                    isTeam 
                      ? isActive ? 'bg-blue-500/20' : 'bg-blue-500/10'
                      : isActive ? 'bg-emerald-500/20' : 'bg-emerald-500/10'
                  }`}>
                    {isTeam ? (
                      <Shield size={24} className={isActive ? 'text-blue-400' : 'text-blue-400/70'} />
                    ) : (
                      <User size={24} className={isActive ? 'text-emerald-400' : 'text-emerald-400/70'} />
                    )}
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 text-left">
                    <p className={`font-heading text-base uppercase ${isActive ? (isTeam ? 'text-blue-400' : 'text-emerald-400') : 'text-foreground'}`}>
                      {isTeam ? ctx.label : 'Private Coach'}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {isTeam ? ctx.description : (ctx.description || 'Personal Training')}
                    </p>
                    {ctx.category && (
                      <span className={`inline-block mt-1 text-xs px-2 py-0.5 rounded ${
                        isTeam ? 'bg-blue-500/10 text-blue-400' : 'bg-emerald-500/10 text-emerald-400'
                      }`}>
                        {ctx.category}
                      </span>
                    )}
                  </div>
                  
                  {/* Arrow */}
                  <ChevronRight size={20} className={`flex-shrink-0 ${
                    isActive ? (isTeam ? 'text-blue-400' : 'text-emerald-400') : 'text-muted-foreground'
                  }`} />
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Invite Coach Modal */}
      {showInviteCoach && (
        <div className="bg-card border border-purple-500/50 p-4 md:p-6 rounded-sm">
          <h3 className="font-heading text-lg uppercase mb-4 flex items-center gap-2">
            {isIndependent ? (
              <>
                <UserPlus className="text-blue-400" size={20} />
                Invite Coach for {athlete?.name}
              </>
            ) : (
              <>
                <RefreshCw className="text-purple-400" size={20} />
                Change Coach for {athlete?.name}
              </>
            )}
          </h3>
          {!isIndependent && (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-sm p-3 mb-4">
              <p className="text-sm text-amber-400">
                <AlertTriangle size={14} className="inline mr-1" />
                Your current coach will lose access to {athlete?.name}'s data. The new coach will be able to see all training history.
              </p>
            </div>
          )}
          <form onSubmit={handleInviteCoach} className="space-y-3">
            <Input
              type="email"
              placeholder="coach@email.com"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              required
            />
            <div className="flex gap-2">
              <Button type="submit" disabled={inviteProcessing} className={`flex-1 ${isIndependent ? 'bg-blue-500 hover:bg-blue-400' : 'bg-purple-500 hover:bg-purple-400'} text-white`}>
                {inviteProcessing ? 'Sending...' : isIndependent ? 'Send Invitation' : 'Send Invite to New Coach'}
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowInviteCoach(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Confirm Migration Modal */}
      {showConfirmMigration && changeRequest && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-sm p-6 max-w-md w-full">
            <h3 className="font-heading text-xl uppercase mb-4 flex items-center gap-2">
              <AlertTriangle className="text-amber-400" size={24} />
              Confirm Coach Change
            </h3>
            
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-sm p-4 mb-4">
              <p className="text-sm text-amber-400 mb-2">
                <strong>Warning:</strong> This action cannot be easily undone.
              </p>
              <p className="text-sm text-muted-foreground">
                The current coach <strong className="text-white">{changeRequest.current_coach_name}</strong> will lose 
                all access to <strong className="text-white">{changeRequest.athlete_name}</strong>'s data.
              </p>
            </div>
            
            <div className="bg-green-500/10 border border-green-500/30 rounded-sm p-4 mb-6">
              <p className="text-sm text-green-400">
                <strong>{changeRequest.new_coach_name}</strong> will become the new primary coach 
                and will have full access to training history.
              </p>
            </div>
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setShowConfirmMigration(false)}
                disabled={migrationProcessing}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                onClick={() => handleConfirmMigration(true)}
                disabled={migrationProcessing}
              >
                {migrationProcessing ? 'Processing...' : 'Confirm Change'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Athlete Pass Card */}
      <div className={`border rounded-sm p-4 md:p-6 ${
        passInfo.status === 'active' ? 'bg-green-500/5 border-green-500/30' :
        passInfo.status === 'pending' ? 'bg-yellow-500/5 border-yellow-500/30' :
        passInfo.status === 'expired' ? 'bg-red-500/5 border-red-500/30' :
        'bg-card border-border/40'
      }`}>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3 md:gap-4">
            <div className={`w-12 h-12 md:w-14 md:h-14 rounded-sm flex items-center justify-center ${
              passInfo.status === 'active' ? 'bg-green-500/20' :
              passInfo.status === 'pending' ? 'bg-yellow-500/20' :
              passInfo.status === 'expired' ? 'bg-red-500/20' :
              'bg-muted/50'
            }`}>
              {isIndependent ? (
                <Star className={`${passInfo.status === 'active' ? 'text-green-400' : 'text-muted-foreground'}`} size={28} />
              ) : (
                <Ticket className={`${
                  passInfo.status === 'active' ? 'text-green-400' :
                  passInfo.status === 'pending' ? 'text-yellow-400' :
                  passInfo.status === 'expired' ? 'text-red-400' :
                  'text-muted-foreground'
                }`} size={28} />
              )}
            </div>
            <div>
              <h2 className="font-heading text-xl uppercase flex items-center gap-2">
                {passLabel}
                <span className={`text-xs px-2 py-1 rounded ${
                  passInfo.status === 'active' ? 'bg-green-500/20 text-green-400' :
                  passInfo.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                  passInfo.status === 'expired' ? 'bg-red-500/20 text-red-400' :
                  'bg-muted text-muted-foreground'
                }`}>
                  {passInfo.text}
                </span>
              </h2>
              <p className="text-muted-foreground text-sm">
                Pass for <span className="text-primary font-medium">{athlete?.name}</span>
              </p>
            </div>
          </div>
          <div className="text-right">
            <div className="flex items-baseline justify-end gap-1 opacity-50">
              <span className="text-sm text-muted-foreground line-through">{passPrice}</span>
              <span className="text-xs text-muted-foreground line-through">/month</span>
            </div>
            <div className="mt-2 inline-flex items-center gap-1.5 bg-primary/10 border border-primary/30 rounded-sm px-2.5 py-1">
              <Sparkles size={10} className="text-primary" />
              <span className="text-[10px] uppercase tracking-widest text-primary font-bold">Included during Founding Beta</span>
            </div>
          </div>
        </div>

        {passInfo.status === 'active' && athletePass && (
          <div className="mt-3 md:mt-4 pt-3 md:pt-4 border-t border-green-500/20">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-green-400">
                <CheckCircle size={16} />
                <span className="text-xs md:text-sm">
                  {athletePass.billing_cycle_end 
                    ? `Valid until ${new Date(athletePass.billing_cycle_end).toLocaleDateString()}`
                    : 'Active subscription'}
                </span>
              </div>
              
              {/* Upgrade to Plus button - only show if not already Plus */}
              {!isIndependent && !isPassPlus && (
                <Button 
                  variant="outline"
                  onClick={() => setShowUpgradeModal(true)}
                  className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10 min-h-[40px]"
                  data-testid="upgrade-to-plus-btn"
                >
                  <Sparkles size={14} className="mr-2" />
                  Upgrade to Plus
                </Button>
              )}
            </div>
          </div>
        )}

        {(passInfo.status === 'pending' || passInfo.status === 'none') && (
          <div className="mt-3 md:mt-4 pt-3 md:pt-4 border-t border-yellow-500/20">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-yellow-400">
                <AlertCircle size={16} />
                <span className="text-xs md:text-sm">Complete payment to access all features for {athlete?.name}</span>
              </div>
              <div className="flex flex-col md:flex-row gap-2">
                <Button 
                  onClick={() => handlePayAthletePass('standard')}
                  disabled={paymentProcessing}
                  className="bg-primary text-black hover:bg-primary/90 min-h-[48px] w-full md:w-auto"
                  data-testid="pay-pass-btn"
                >
                  <CreditCard size={16} className="mr-2" />
                  {paymentProcessing ? 'Processing...' : `Pay ${passPrice}/month`}
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => setShowUpgradeModal(true)}
                  className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10 min-h-[48px] w-full md:w-auto"
                  data-testid="choose-plus-btn"
                >
                  <Sparkles size={14} className="mr-2" />
                  Or get Plus
                </Button>
              </div>
            </div>
          </div>
        )}

        {passInfo.status === 'expired' && (
          <div className="mt-3 md:mt-4 pt-3 md:pt-4 border-t border-red-500/20">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-red-400">
                <AlertCircle size={16} />
                <span className="text-xs md:text-sm">Pass expired for {athlete?.name}. Renew to restore access.</span>
              </div>
              <div className="flex flex-col md:flex-row gap-2">
                <Button 
                  onClick={() => handlePayAthletePass('standard')}
                  disabled={paymentProcessing}
                  className="bg-red-500 text-white hover:bg-red-600 min-h-[48px] w-full md:w-auto"
                >
                  <CreditCard size={16} className="mr-2" />
                  {paymentProcessing ? 'Processing...' : 'Renew Pass'}
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => setShowUpgradeModal(true)}
                  className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10 min-h-[48px] w-full md:w-auto"
                >
                  <Sparkles size={14} className="mr-2" />
                  Renew with Plus
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Team Information Section - Show when athlete is part of a team */}
      {athlete && athleteTeams[athlete.id]?.length > 0 && (
        <div className="bg-gradient-to-r from-blue-500/5 to-blue-500/10 border border-blue-500/30 rounded-sm overflow-hidden" data-testid="athlete-teams-section">
          <div className="p-4 border-b border-blue-500/20">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-500/20 rounded-full flex items-center justify-center">
                  <Building2 className="text-blue-400" size={20} />
                </div>
                <div>
                  <h3 className="font-heading text-lg uppercase">My Teams</h3>
                  <p className="text-xs text-muted-foreground">{athlete.name} is part of {athleteTeams[athlete.id].length} team{athleteTeams[athlete.id].length > 1 ? 's' : ''}</p>
                </div>
              </div>
            </div>
          </div>
          <div className="p-4 space-y-3">
            {athleteTeams[athlete.id].map((team) => (
              <div 
                key={team.team_id}
                className="bg-card border border-border/40 rounded-sm p-4 hover:border-blue-500/50 transition-colors"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-blue-500/10 rounded-full flex items-center justify-center">
                      <Shield className="text-blue-400" size={22} />
                    </div>
                    <div>
                      <p className="font-heading text-lg">{team.team_name}</p>
                      <p className="text-xs text-muted-foreground">
                        {team.club_name} • {team.category}
                      </p>
                    </div>
                  </div>
                  <Button 
                    size="sm"
                    variant="outline"
                    className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                    onClick={() => navigate(`/dashboard/team/${team.team_id}`)}
                    data-testid={`view-team-btn-${team.team_id}`}
                  >
                    <ExternalLink size={14} className="mr-1" />
                    View Team
                  </Button>
                </div>
                
                {/* Coaches */}
                {team.coaches?.length > 0 && (
                  <div className="mb-3">
                    <p className="text-xs text-muted-foreground mb-1">Coaches</p>
                    <div className="flex flex-wrap gap-2">
                      {team.coaches.map((coach, idx) => (
                        <span 
                          key={idx}
                          className={`text-xs px-2 py-1 rounded-sm ${
                            coach.role === 'head_coach' 
                              ? 'bg-blue-500/20 text-blue-400' 
                              : 'bg-muted text-muted-foreground'
                          }`}
                        >
                          {coach.name} {coach.role === 'head_coach' && '(Head)'}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Next Session */}
                {team.next_session && (
                  <div className="bg-muted/30 rounded-sm p-3">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                      <Calendar size={12} />
                      <span>Next Session</span>
                    </div>
                    <p className="font-medium text-sm">{team.next_session.title || 'Training Session'}</p>
                    <p className="text-xs text-blue-400">
                      {new Date(team.next_session.date).toLocaleDateString('en-US', {
                        weekday: 'long',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                )}

                {!team.next_session && (
                  <div className="bg-muted/30 rounded-sm p-3 text-center">
                    <p className="text-xs text-muted-foreground">No upcoming sessions scheduled</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Training Plan Info Card - Show when athlete has a coach */}
      {athlete && !isIndependent && planInfo && (
        <div className="bg-card border border-border/40 rounded-sm p-4 md:p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-primary/10 rounded-sm flex items-center justify-center">
              <Package className="text-primary" size={20} />
            </div>
            <div>
              <h3 className="font-heading text-lg uppercase">Training Plan</h3>
              <p className="text-xs text-muted-foreground">Plan information for {athlete?.name}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {/* Plan Type */}
            <div className="bg-muted/30 rounded-sm p-3">
              <div className="flex items-center gap-2 mb-1">
                <ClipboardList size={14} className="text-muted-foreground" />
                <span className="text-xs text-muted-foreground uppercase tracking-wider">Plan</span>
              </div>
              <p className="font-semibold text-sm md:text-base">{planInfo.planName}</p>
            </div>

            {/* Plan Value */}
            <div className="bg-muted/30 rounded-sm p-3">
              <div className="flex items-center gap-2 mb-1">
                <DollarSign size={14} className="text-muted-foreground" />
                <span className="text-xs text-muted-foreground uppercase tracking-wider">Value</span>
              </div>
              <p className="font-semibold text-sm md:text-base">
                {planInfo.planValue > 0 ? `$${planInfo.planValue.toFixed(2)}` : 'To be defined'}
              </p>
            </div>

            {/* Payment Status */}
            <div className="bg-muted/30 rounded-sm p-3">
              <div className="flex items-center gap-2 mb-1">
                <CreditCard size={14} className="text-muted-foreground" />
                <span className="text-xs text-muted-foreground uppercase tracking-wider">Payment</span>
              </div>
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
                planInfo.paymentStatus === 'paid' 
                  ? 'bg-green-500/20 text-green-400' 
                  : planInfo.paymentStatus === 'overdue'
                  ? 'bg-red-500/20 text-red-400'
                  : 'bg-yellow-500/20 text-yellow-400'
              }`}>
                {planInfo.paymentStatus === 'paid' && <CheckCircle size={12} />}
                {planInfo.paymentStatus === 'pending' && <Clock size={12} />}
                {planInfo.paymentStatus === 'overdue' && <AlertCircle size={12} />}
                {planInfo.paymentStatus === 'paid' ? 'Paid' : 
                 planInfo.paymentStatus === 'overdue' ? 'Overdue' : 'Pending'}
              </span>
            </div>

            {/* Plan Validity */}
            <div className="bg-muted/30 rounded-sm p-3">
              <div className="flex items-center gap-2 mb-1">
                <Calendar size={14} className="text-muted-foreground" />
                <span className="text-xs text-muted-foreground uppercase tracking-wider">Validity</span>
              </div>
              {planInfo.planEndDate ? (
                <div>
                  <p className="font-semibold text-sm md:text-base">
                    {planInfo.planEndDate.toLocaleDateString('en-US')}
                  </p>
                  {planInfo.validityStatus === 'active' && planInfo.daysRemaining !== null && (
                    <p className="text-xs text-green-400">
                      {planInfo.daysRemaining} days remaining
                    </p>
                  )}
                  {planInfo.validityStatus === 'expired' && (
                    <p className="text-xs text-red-400">Expired</p>
                  )}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Not defined</p>
              )}
            </div>
          </div>

          {/* Sessions Progress Bar */}
          {planInfo.sessionsTotal > 0 && (
            <div className="mt-4 pt-4 border-t border-border/40">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">Sessões utilizadas</span>
                <span className="text-sm font-medium">
                  {planInfo.sessionsTotal - planInfo.sessionsRemaining} / {planInfo.sessionsTotal}
                </span>
              </div>
              <div className="w-full h-2 bg-muted/50 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-primary transition-all duration-500"
                  style={{ 
                    width: `${Math.min(((planInfo.sessionsTotal - planInfo.sessionsRemaining) / planInfo.sessionsTotal) * 100, 100)}%` 
                  }}
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {planInfo.sessionsRemaining} sessões restantes
              </p>
            </div>
          )}

          {/* Period Info */}
          {planInfo.planStartDate && planInfo.planEndDate && (
            <div className="mt-3 text-xs text-muted-foreground text-center">
              Período: {planInfo.planStartDate.toLocaleDateString('pt-BR')} até {planInfo.planEndDate.toLocaleDateString('pt-BR')}
            </div>
          )}
        </div>
      )}

      {/* Progress Section - Show only when pass is active */}
      {passInfo.status === 'active' && (
        <div className="grid md:grid-cols-2 gap-4">
          {/* Training Progress */}
          <div className="bg-card border border-border/40 p-4 md:p-6 rounded-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-primary/10 rounded-sm flex items-center justify-center">
                <TrendingUp className="text-primary" size={20} />
              </div>
              <div>
                <h3 className="font-semibold text-sm md:text-base">Training Progress</h3>
                <p className="text-xs text-muted-foreground">Sessions for {athlete?.name}</p>
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-2 md:gap-4 mb-4">
              <div className="text-center p-2 md:p-3 bg-muted/30 rounded-sm">
                <p className="text-xl md:text-2xl font-heading text-green-400">{completedSessions}</p>
                <p className="text-xs text-muted-foreground">Done</p>
              </div>
              <div className="text-center p-2 md:p-3 bg-muted/30 rounded-sm">
                <p className="text-xl md:text-2xl font-heading text-blue-400">{scheduledSessions}</p>
                <p className="text-xs text-muted-foreground">Scheduled</p>
              </div>
              <div className="text-center p-2 md:p-3 bg-muted/30 rounded-sm">
                <p className="text-xl md:text-2xl font-heading">{totalSessions}</p>
                <p className="text-xs text-muted-foreground">Total</p>
              </div>
            </div>

            {nextMilestone && !nextMilestone.achieved && (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs md:text-sm">
                  <span className="text-muted-foreground">Next: {nextMilestone.name}</span>
                  <span className="text-primary font-medium">{completedSessions}/{nextMilestone.target}</span>
                </div>
                <div className="w-full h-2 bg-muted/50 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-primary transition-all duration-500"
                    style={{ width: `${Math.min((completedSessions / nextMilestone.target) * 100, 100)}%` }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Milestones */}
          <div className="bg-card border border-border/40 p-4 md:p-6 rounded-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-primary/10 rounded-sm flex items-center justify-center">
                <Award className="text-primary" size={20} />
              </div>
              <div>
                <h3 className="font-semibold text-sm md:text-base">Milestones</h3>
                <p className="text-xs text-muted-foreground">{achievedMilestones}/4 achieved</p>
              </div>
            </div>
            <div className="grid grid-cols-4 gap-2">
              {milestones.map((milestone) => (
                <div 
                  key={milestone.id}
                  className={`text-center p-2 rounded-sm ${milestone.achieved ? 'bg-primary/10' : 'bg-muted/30 opacity-50'}`}
                >
                  <span className="text-xl">{milestone.icon}</span>
                  <p className="text-xs mt-1">{milestone.name}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Quick Links */}
      <div className="grid md:grid-cols-2 gap-4">
        {quickLinks.map((link) => (
          <Link
            key={link.path}
            to={link.path}
            className={`bg-card border border-border/40 p-4 md:p-6 rounded-sm transition-colors group ${
              needsPayment ? 'opacity-50 pointer-events-none' : 'hover:border-primary/50'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 md:gap-4">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-primary/10 rounded-sm flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                  <link.icon className="text-primary" size={20} />
                </div>
                <div>
                  <h3 className="font-semibold text-sm md:text-base">{link.label}</h3>
                  <p className="text-xs md:text-sm text-muted-foreground">{link.desc}</p>
                </div>
              </div>
              <ArrowRight className="text-muted-foreground group-hover:text-primary transition-colors" size={20} />
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};

export default ParentDashboard;
